import { formatMinutes } from "./dailyBoardUtils";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function localDateKey(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function dayBounds(dateStr) {
  const start = new Date(`${dateStr}T00:00:00`);
  const end = new Date(`${dateStr}T23:59:59.999`);
  return { start, end };
}

function isSameLocalDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function buildDayWork(entries, dateStr, now = new Date()) {
  const { start: dayStart, end: dayEnd } = dayBounds(dateStr);
  const isToday = isSameLocalDay(dayStart, now);

  const sorted = [...entries]
    .filter((e) => {
      const t = new Date(e.recordedAt);
      return t >= dayStart && t <= dayEnd;
    })
    .sort((a, b) => new Date(a.recordedAt) - new Date(b.recordedAt));

  let workStart = null;
  let workedMinutes = 0;
  const closingEntryIds = new Map();

  for (const entry of sorted) {
    const at = new Date(entry.recordedAt);

    if (entry.eventType === "clock_in") {
      workStart = entry.recordedAt;
    } else if (entry.eventType === "pause_start" && workStart) {
      const mins = Math.max(0, (at - new Date(workStart)) / 60000);
      workedMinutes += mins;
      closingEntryIds.set(entry.id, Math.round(mins));
      workStart = null;
    } else if (entry.eventType === "pause_end") {
      workStart = entry.recordedAt;
    } else if (entry.eventType === "clock_out" && workStart) {
      const mins = Math.max(0, (at - new Date(workStart)) / 60000);
      workedMinutes += mins;
      closingEntryIds.set(entry.id, Math.round(mins));
      workStart = null;
    }
  }

  if (workStart && isToday) {
    workedMinutes += Math.max(0, (now - new Date(workStart)) / 60000);
  }

  return {
    workedMinutes: Math.round(workedMinutes),
    closingEntryIds,
  };
}

function formatDayChipLabel(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

function formatTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

function sessionWorkedMinutes(clockIn, clockOut, pauses = [], now = new Date()) {
  if (!clockIn) return 0;

  let total = 0;
  let workStart = clockIn.recordedAt;

  const events = [
    ...pauses.flatMap((p) => [
      p.start ? { at: p.start.recordedAt, type: "pause_start" } : null,
      p.end ? { at: p.end.recordedAt, type: "pause_end" } : null,
    ]),
    clockOut
      ? { at: clockOut.recordedAt, type: "clock_out" }
      : { at: now.toISOString(), type: "clock_out" },
  ].filter(Boolean);

  events.sort((a, b) => new Date(a.at) - new Date(b.at));

  for (const ev of events) {
    if (ev.type === "pause_start" && workStart) {
      total += Math.max(0, (new Date(ev.at) - new Date(workStart)) / 60000);
      workStart = null;
    } else if (ev.type === "pause_end") {
      workStart = ev.at;
    } else if (ev.type === "clock_out" && workStart) {
      total += Math.max(0, (new Date(ev.at) - new Date(workStart)) / 60000);
      workStart = null;
    }
  }

  return Math.round(total);
}

function sessionStatus(session) {
  const entries = [session.clockIn, session.clockOut].filter(Boolean);
  if (entries.some((e) => e.isCorrection)) return "correction";
  if (session.isOpen) return "open";
  if (entries.some((e) => e.source === "manual_correction" && e.correctionReason)) {
    return "manual";
  }
  return "ok";
}

function buildHistorySessions(rows, now = new Date()) {
  const active = rows
    .filter((r) => !r.isSuperseded)
    .sort((a, b) => new Date(a.recordedAt) - new Date(b.recordedAt));

  const byUser = new Map();
  active.forEach((entry) => {
    if (!byUser.has(entry.userId)) byUser.set(entry.userId, []);
    byUser.get(entry.userId).push(entry);
  });

  const sessions = [];

  for (const userEntries of byUser.values()) {
    let current = null;

    const flush = () => {
      if (!current) return;
      sessions.push({
        ...current,
        workedMinutes: sessionWorkedMinutes(
          current.clockIn,
          current.clockOut,
          current.pauses,
          now,
        ),
        status: sessionStatus(current),
      });
      current = null;
    };

    for (const entry of userEntries) {
      if (entry.eventType === "clock_in") {
        flush();
        current = {
          id: `session-in-${entry.id}`,
          userId: entry.userId,
          employee: entry.employee,
          clockIn: entry,
          clockOut: null,
          pauses: [],
          isOpen: true,
        };
      } else if (entry.eventType === "pause_start" && current) {
        current.pauses.push({ start: entry, end: null });
      } else if (entry.eventType === "pause_end" && current?.pauses.length) {
        const openPause = [...current.pauses].reverse().find((p) => p.start && !p.end);
        if (openPause) openPause.end = entry;
      } else if (entry.eventType === "clock_out") {
        if (current) {
          current.clockOut = entry;
          current.isOpen = false;
          flush();
        } else {
          sessions.push({
            id: `session-out-${entry.id}`,
            userId: entry.userId,
            employee: entry.employee,
            clockIn: null,
            clockOut: entry,
            pauses: [],
            isOpen: false,
            workedMinutes: 0,
            status: entry.isCorrection ? "correction" : "ok",
          });
        }
      }
    }

    flush();
  }

  sessions.sort((a, b) => {
    const aDate = a.clockIn?.recordedAt || a.clockOut?.recordedAt;
    const bDate = b.clockIn?.recordedAt || b.clockOut?.recordedAt;
    return new Date(bDate) - new Date(aDate);
  });

  return sessions;
}

function computeHistoryWorkStats(rows, now = new Date()) {
  const active = rows
    .filter((r) => !r.isSuperseded)
    .sort((a, b) => new Date(a.recordedAt) - new Date(b.recordedAt));

  const byUser = new Map();
  active.forEach((entry) => {
    const uid = entry.userId;
    if (!byUser.has(uid)) byUser.set(uid, []);
    byUser.get(uid).push(entry);
  });

  const dailyTotals = new Map();
  const employeeTotals = [];
  const entryDurations = new Map();
  let totalMinutes = 0;

  for (const [userId, entries] of byUser) {
    const dates = new Set(entries.map((e) => localDateKey(e.recordedAt)));
    let userMinutes = 0;
    const username =
      entries.find((e) => e.employee?.username)?.employee?.username || userId;

    dates.forEach((dateStr) => {
      const { workedMinutes, closingEntryIds } = buildDayWork(entries, dateStr, now);
      userMinutes += workedMinutes;
      dailyTotals.set(dateStr, (dailyTotals.get(dateStr) || 0) + workedMinutes);
      closingEntryIds.forEach((mins, id) => entryDurations.set(id, mins));
    });

    totalMinutes += userMinutes;
    if (userMinutes > 0) {
      employeeTotals.push({ userId, username, workedMinutes: userMinutes });
    }
  }

  const dailyChips = [...dailyTotals.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([dateStr, minutes]) => ({
      dateStr,
      label: formatDayChipLabel(dateStr),
      minutes,
    }));

  employeeTotals.sort((a, b) => a.username.localeCompare(b.username, "es"));

  return {
    totalMinutes,
    dailyChips,
    employeeTotals,
    entryDurations,
    formatMinutes,
  };
}

export {
  computeHistoryWorkStats,
  buildHistorySessions,
  formatMinutes,
  formatTime,
  formatDate,
};
